/**
 * Main App Component
 * Root component with routing, theming, and auth
 */

import React, { useEffect } from "react";
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { HelpContentProvider } from "./contexts/HelpContentContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "./store";
import { checkAuthStatus } from "./store/authSlice";
import { createAppTheme } from "./theme/muiTheme";

// Components
import Layout from "./components/layout/Layout";
import DashboardOverview from "./components/dashboard/DashboardOverview";
import UserManagement from "./components/user-management/UserManagement";
import AdminUserManagement from "./components/user-management/AdminUserManagement";
import ContentManagement from "./components/content/ContentManagement";
import CostManagement from "./components/cost/CostManagement";
import SystemMonitoring from "./components/monitoring/SystemMonitoring";
import CommunicationsHubV2 from "./components/communications-v2/CommunicationsHubV2";
import FamilyCollaborationDashboard from "./components/family/FamilyCollaborationDashboard";
import { SystemAdministration } from "./components/system";
import ProfileSettings from "./components/profile/ProfileSettings";
import AITestingDashboard from "./components/ai-testing/AITestingDashboard";
import ProductIntelligence from "./components/product-intelligence/ProductIntelligence";
import MmpAnalyticsPage from "./components/store-analytics/MmpAnalyticsPage";
import NotificationJourneyPage from "./components/store-analytics/NotificationJourneyPage";
import WebsiteAnalyticsPage from "./components/website-analytics/WebsiteAnalyticsPage";
import GoogleAdsLandingPage from "./components/google-ads/GoogleAdsLandingPage";
import BriefingPage from "./components/briefing/BriefingPage";
import AdDashboardPage from "./components/ads/AdDashboardPage";
import HomeRedirect from "./components/routing/HomeRedirect";
import AdReportPage from "./components/adreport/AdReportPage";
import StoreClickAnalyticsPage from "./components/store-analytics/StoreClickAnalyticsPage";
import SurveyTrackingPage from "./components/survey-tracking/SurveyTrackingPage";
import BrainHealthDashboard from "./components/brain-health/BrainHealthDashboard";
import GrokImaginePromptsManagement from "./components/ai/GrokImaginePromptsManagement";
import StripeDiscounts from "./components/revenue/StripeDiscounts";
import RevenueSubscriptionsPage from "./components/revenue/RevenueSubscriptionsPage";
import PromotionsDiscountsPage from "./components/promotions/PromotionsDiscountsPage";
import CustomerSupport from "./components/customer-support/CustomerSupport";
import FreeTierStandalonePage from "./components/user-management/FreeTierStandalonePage";
import FunnelManagerPage from "./components/communications-v2/marketing/funnels/FunnelManagerPage";
import LoginPage from "./pages/LoginPage";
import RoleGuard from "./components/auth/RoleGuard";
import MfaSettings from "./components/security/MfaSettings";
import QuillSection from "./components/quill/QuillSection";
import LifeAdviceSection from "./components/lifeadvice/LifeAdviceSection";
import BotOpsRoutes from "./components/bot-ops/BotOpsRoutes";
import WeddingSection from "./components/wedding/WeddingSection";
import TestAccountReviewPage from './components/user-management/TestAccountReviewPage';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // TEMPORARY FIX: Don't redirect if user seems to have been authenticated recently
  // This prevents auth race condition redirects while preserving security
  if (!isAuthenticated) {
    console.log(
      "🔐 ProtectedRoute: User not authenticated, checking for recent activity...",
    );

    // Check if we're in an active session (has cookies, recent activity, etc.)
    const hasAuthCookies =
      document.cookie.includes("admin_access_token") ||
      document.cookie.includes("admin_refresh_token");

    if (hasAuthCookies) {
      console.log(
        "🔐 ProtectedRoute: Auth cookies found, allowing access despite auth state mismatch",
      );
      return <>{children}</>;
    }

    console.log(
      "🔐 ProtectedRoute: No auth cookies found, redirecting to login",
    );
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Main App component with Redux
const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useSelector((state: RootState) => state.ui);
  const { isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    let isMounted = true;
    let hasChecked = false;

    // Only check auth status once when component mounts
    const checkAuth = async () => {
      if (isMounted && !hasChecked) {
        console.log("🔐 [APP] Checking authentication status on mount");
        hasChecked = true;
        dispatch(checkAuthStatus());
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []); // Remove dispatch dependency to prevent infinite loop

  const muiTheme = React.useMemo(() => createAppTheme(theme), [theme]);

  const globalStyles = (
    <GlobalStyles
      styles={{
        "*": {
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
        },
        html: {
          height: "100%",
        },
        body: {
          height: "100%",
          fontFamily:
            '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        "#root": {
          height: "100%",
        },
      }}
    />
  );

  if (isLoading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {globalStyles}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {globalStyles}
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/dashboard" element={<RoleGuard requiredResource="overview"><DashboardOverview /></RoleGuard>} />
                    <Route path="/briefing" element={<Navigate to="/briefing/daily" replace />} />
                    <Route path="/briefing/:kind" element={<RoleGuard requiredResource="overview"><BriefingPage /></RoleGuard>} />
                    <Route path="/ad-report" element={<RoleGuard requiredResource="subscription_analytics"><AdReportPage /></RoleGuard>} />
                    <Route path="/ads" element={<RoleGuard requiredResource="subscription_analytics"><AdDashboardPage /></RoleGuard>} />
                    {/* Grokbot (Bot Operations). The server enforces bot_operations too; this guard is only the UI half. */}
                    <Route path="/dashboard/bot-ops/*" element={<RoleGuard requiredResource="bot_operations"><BotOpsRoutes /></RoleGuard>} />
                    <Route path="/grokbot" element={<Navigate to="/dashboard/bot-ops" replace />} />
                    <Route
                      path="/dashboard/users"
                      element={<RoleGuard requiredResource="user_management"><UserManagement /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/admin-users"
                      element={<RoleGuard requiredResource="admin_users"><AdminUserManagement /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/test-accounts"
                      element={<RoleGuard requiredResource="user_management"><TestAccountReviewPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/free-tier"
                      element={<RoleGuard requiredResource="free_tier_users"><FreeTierStandalonePage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/brain-health"
                      element={<RoleGuard requiredResource="brain_health"><BrainHealthDashboard /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/grok-imagine"
                      element={<RoleGuard requiredResource="grok_imagine"><GrokImaginePromptsManagement /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/revenue"
                      element={<Navigate to="/dashboard/revenue-subscriptions" replace />}
                    />
                    <Route
                      path="/dashboard/stripe-discounts"
                      element={<Navigate to="/dashboard/promotions" replace />}
                    />
                    <Route
                      path="/dashboard/subscriptions"
                      element={<Navigate to="/dashboard/revenue-subscriptions" replace />}
                    />
                    <Route
                      path="/dashboard/subscription-intelligence"
                      element={<Navigate to="/dashboard/revenue-subscriptions" replace />}
                    />
                    <Route
                      path="/dashboard/revenue-subscriptions"
                      element={<RoleGuard requiredResources={["revenue_analytics", "subscription_analytics"]}><RevenueSubscriptionsPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/mmp-analytics"
                      element={<RoleGuard requiredResource="subscription_analytics"><MmpAnalyticsPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/notification-journey"
                      element={<RoleGuard requiredResource="subscription_analytics"><NotificationJourneyPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/website-analytics"
                      element={<RoleGuard requiredResource="subscription_analytics"><WebsiteAnalyticsPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/google-ads-landing"
                      element={<RoleGuard requiredResource="subscription_analytics"><GoogleAdsLandingPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/store-clicks"
                      element={<RoleGuard requiredResource="customer_analytics"><StoreClickAnalyticsPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/survey-tracking"
                      element={<RoleGuard requiredResource="audience_manager"><SurveyTrackingPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/promotions"
                      element={<RoleGuard requiredResource="stripe_discounts"><PromotionsDiscountsPage /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/customer-support"
                      element={<RoleGuard requiredResource="customer_support"><CustomerSupport /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/content/*"
                      element={<RoleGuard requiredResource="chapter_creation"><ContentManagement /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/quill/*"
                      element={<RoleGuard requiredResource="quill_admin"><QuillSection /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/lifeadvice/*"
                      element={<RoleGuard requiredResource="lifeadvice_admin"><LifeAdviceSection /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/wedding/*"
                      element={<RoleGuard requiredResource="wedding_books"><WeddingSection /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/cost-management/*"
                      element={<RoleGuard requiredResource="cost_management"><CostManagement /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/monitoring/*"
                      element={<RoleGuard requiredResource="aws_cloudwatch"><SystemMonitoring /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/email-funnels"
                      element={
                        <Navigate
                          to="/dashboard/communications/marketing/funnels"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/dashboard/email-funnels/*"
                      element={
                        <Navigate
                          to="/dashboard/communications/marketing/funnels"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/dashboard/communications/marketing/funnels/*"
                      element={
                        <RoleGuard requiredResources={["free_tier_users", "comms_overview"]}>
                          <FunnelManagerPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/dashboard/communications/*"
                      element={<RoleGuard requiredResource="comms_overview"><CommunicationsHubV2 /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/family"
                      element={<RoleGuard requiredResource="family_collaboration"><FamilyCollaborationDashboard /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/system/*"
                      element={<RoleGuard requiredResource="audit_log"><SystemAdministration /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/profile"
                      element={<ProfileSettings />}
                    />
                    {/* Self-service: any authenticated admin manages their own 2FA (no resource gate) */}
                    <Route
                      path="/dashboard/security"
                      element={<MfaSettings />}
                    />
                    <Route
                      path="/dashboard/ai-testing"
                      element={<RoleGuard requiredResource="ai_monitor"><AITestingDashboard /></RoleGuard>}
                    />
                    <Route
                      path="/dashboard/product-intelligence"
                      element={<RoleGuard requiredResource="product_intelligence"><ProductIntelligence /></RoleGuard>}
                    />
                    <Route path="*" element={<HomeRedirect />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

// Root App component with Provider
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <HelpContentProvider>
        <AppContent />
      </HelpContentProvider>
    </Provider>
  );
};

export default App;
